import { useState } from 'react';

export default function KISolutions() {
  const [isExpanded, setIsExpanded] = useState(false);

  const solutions = [
    {
      title: 'KI Chat Bots',
      description: 'Ob intern oder extern – unsere dialogbasierte KI beantwortet Fragen 24/7 und übernimmt gleichzeitig eine effiziente Lead-Qualifizierung.',
      icon: 'ri-robot-line',
      image: null,
      features: ['24/7 Verfügbarkeit', 'Lead-Qualifizierung', 'Mehrsprachig']
    },
    {
      title: 'Email Marketing per KI',
      description: 'Unsere personalisierten E-Mail-Kampagnen ermöglichen es Ihnen, mit minimalem Aufwand die Kundenbindung zu erhöhen und zusätzliche Neukunden zu erreichen.',
      icon: 'ri-mail-ai-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20email%20envelope%20icon%20with%20AI%20technology%20elements%20glowing%20teal%20and%20blue%20accents%20clean%20minimalist%20design%20on%20dark%20background%20digital%20marketing%20concept%20futuristic%20communication%20symbol%20professional%20business%20style&width=200&height=200&seq=email-icon&orientation=squarish',
      features: ['Personalisierung', 'Automatisierung', 'Höhere Conversion']
    },
    {
      title: 'Social Media Automatisieren',
      description: 'Social Media wird dann einfach, wenn das richtige Tool Ihnen den Großteil der Arbeit abnimmt.',
      icon: 'ri-share-circle-line',
      image: null,
      features: ['Content-Planung', 'Auto-Posting', 'Analytics']
    }
  ];

  return (
    <section id="ki-solutions" className="py-20 bg-gray-900/50 relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: 'url("https://static.readdy.ai/image/543e6025ab8aef1bb1f6ad8fb7713dea/938f2135c5f64d09cf0225d680c632a5.png")'
        }}
      >
        <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-none"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Unsere <span className="text-teal-400">KI Lösungen</span>
          </h2>
          
          {/* Toggle Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-3 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <span>{isExpanded ? 'Weniger anzeigen' : 'Mehr erfahren'}</span>
            <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}></i>
          </button>
        </div>

        {/* Collapsible Content */}
        <div 
          className={`transition-all duration-500 ease-in-out overflow-hidden ${
            isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pt-8">
            <p className="text-xl text-gray-300 max-w-3xl mx-auto text-center mb-16">
              Entdecken Sie unsere innovativen KI-Technologien, die Ihr Unternehmen automatisieren und Ihre Effizienz steigern.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {solutions.map((solution, index) => (
                <div 
                  key={index}
                  className="bg-gray-800/80 backdrop-blur-md p-8 rounded-xl border border-gray-700 hover:border-teal-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group"
                >
                  {solution.image ? (
                    <div className="w-16 h-16 rounded-lg overflow-hidden mb-6">
                      <img 
                        src={solution.image} 
                        alt={solution.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg flex items-center justify-center mb-6 group-hover:from-teal-500 group-hover:to-teal-600 transition-all duration-300">
                      <i className={`${solution.icon} text-2xl text-white`}></i>
                    </div>
                  )}
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors duration-300">
                    {solution.title}
                  </h3>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {solution.description}
                  </p>
                  
                  <div className="space-y-2">
                    {solution.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-400">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mr-3"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-gray-800/90 to-gray-900/90 backdrop-blur-md p-12 rounded-2xl border border-gray-700 text-center">
              <h3 className="text-3xl font-bold text-white mb-6 text-center">
                Bereit für KI-gestützte Automatisierung?
              </h3>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Lassen Sie uns gemeinsam analysieren, wie unsere KI-Lösungen Ihr Unternehmen transformieren können. 
                Buchen Sie jetzt Ihr kostenloses Beratungsgespräch.
              </p>
              <div className="flex justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  Kostenlose Beratung buchen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}